<template>
  <v-card :loading="isLoading">
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form">
        <v-container fluid>
          <v-row>
            <v-col md="6" cols="12">
              <div class="label mb-2">Lesson Name</div>
              <v-text-field
                v-model="form.name"
                :rules="[validationRules.required]"
                solo
                flat
                outlined
                dense
                placeholder="Lesson name"
              />
            </v-col>
            <v-col md="6" cols="12">
              <div class="label mb-2">Lesson Name (Chinese)</div>
              <v-text-field
                v-model="form.name_cn"
                solo
                flat
                outlined
                dense
                placeholder="Lesson name"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12">
              <div class="label mb-2">Lesson Description</div>
              <v-textarea
                v-model="form.description"
                solo
                flat
                outlined
                dense
                auto-grow
                placeholder="Lesson description"
              />
            </v-col>
            <v-col md="6" cols="12">
              <div class="label mb-2">Lesson Description (Chinese)</div>
              <v-textarea
                v-model="form.description_cn"
                solo
                flat
                outlined
                dense
                auto-grow
                placeholder="Lesson description"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4">
              <div class="label mb-2">Term</div>
              <v-text-field
                v-model="form.term"
                type="number"
                :rules="[validationRules.required]"
                solo
                flat
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <div class="label mb-2">Month</div>
              <v-text-field
                v-model="form.month"
                type="number"
                :rules="[validationRules.required]"
                solo
                flat
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="4">
              <div class="label mb-2">Session</div>
              <v-text-field
                v-model="form.session"
                type="number"
                :rules="[validationRules.required]"
                solo
                flat
                outlined
                dense
              />
            </v-col>
          </v-row>
          <div class="label mb-2">Lesson Category</div>
          <v-select
            v-model="form.lesson_category_id"
            :items="lessonCategories"
            :loading="isCategoryListLoading"
            item-value="id"
            :item-text="(item) => bilingualText(item, 'name', 'name_cn')"
            solo
            flat
            outlined
            dense
            placeholder="Lesson category"
          />
          <div class="label mb-2">Learning Material Files</div>
          <v-row class="my-4">
            <v-col
              v-for="(attachment, attIndex) in form.attachments"
              :key="attIndex"
              lg="3"
              md="4"
              cols="12"
            >
              <v-responsive :aspect-ratio="3 / 2">
                <file-uploader
                  ref="attachmentFiles"
                  :overlay-menu="true"
                  :value="attachment"
                  :disabled="isReadonly"
                  @input="updateAttachment(attIndex, $event)"
                >
                  <template #overlay-menu>
                    <v-container fluid class="fill-height">
                      <v-row class="my-auto">
                        <v-col cols="12" class="text-center">
                          <v-btn
                            small
                            light
                            width="120"
                            @click="$refs.attachmentFiles[j].browseFiles()"
                          >
                            Change
                          </v-btn>
                        </v-col>
                        <v-col cols="12" class="text-center">
                          <v-btn
                            small
                            width="120"
                            color="error"
                            @click="removeAttachment(attIndex)"
                          >
                            Remove
                          </v-btn>
                        </v-col>
                      </v-row>
                    </v-container>
                  </template>
                </file-uploader>
              </v-responsive>
            </v-col>
            <v-col lg="3" md="4" cols="12">
              <file-uploader
                :enable-preview="false"
                @input="addAttachment($event)"
              ></file-uploader>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col cols="6" md="3">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col cols="6" md="3">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isLoading"
              @click="submit"
            >
              Submit
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
export default {
  name: 'LessonForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    lesson: {
      type: Object,
      default: null,
      required: false,
    },
    submitApi: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      isLoading: false,
      form: {
        name: null,
        name_cn: null,
        description: null,
        description_cn: null,
        lesson_category_id: null,
        term: 1,
        month: 1,
        session: 1,
        attachments: [],
      },
    }
  },
  computed: {
    ...mapGetters({
      categoryList: 'lessonCategory/list',
      isCategoryListLoading: 'lessonCategory/isListLoading',
    }),
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    lessonCategories() {
      return this.categoryList.data
    },
    isReadonly() {
      return this.mode === 'view'
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...mapActions({
      upload: 'storage/upload',
      getCategoryList: 'lessonCategory/getList',
    }),
    async init() {
      if (this.lessonCategories.length === 0) {
        await this.getCategoryList()
      }
      if (this.mode != 'add' && this.lesson != null) {
        _.merge(this.form, this.lesson)
        this.form.attachments = this.lesson.attachment_file_url
          ? this.lesson.attachment_file_url.split(',')
          : []
      }
    },
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.lesson) {
        console.error(
          `lesson props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async updateAttachment(attIndex, file) {
      this.isLoading = true
      const responseData = await this.upload({ file, baseDir: 'lesson-plan' })
      if (responseData) {
        this.form.attachments.splice(attIndex, 1, responseData.url)
      }
      this.isLoading = false
    },
    removeAttachment(attIndex) {
      this.form.attachments.splice(attIndex, 1)
    },
    async addAttachment(file) {
      this.isLoading = true
      const responseData = await this.upload({ file, baseDir: 'lesson-plan' })
      if (responseData) {
        this.form.attachments.push(responseData.url)
      }
      this.isLoading = false
    },
    resetForm() {
      this.form = {
        name: null,
        name_cn: null,
        description: null,
        description_cn: null,
        lesson_category_id: null,
        term: 1,
        month: 1,
        session: 1,
        attachments: [],
      }
      this.$refs.form.resetValidation()
    },
    cancel() {
      this.$emit('cancel', true)
    },
    submit() {
      if (this.$refs.form.validate()) {
        const lesson = _.cloneDeep(this.form)
        lesson.attachment_file_url =
          this.form.attachments && this.form.attachments.length > 0
            ? this.form.attachments.join(',')
            : null
        if (!this.submitApi) {
          this.$emit('submit', lesson)
        }
        this.resetForm()
      }
    },
  },
}
</script>
